import type { ParsedParam } from "@/lib/mcp/schema";

/**
 * Build default values map for parameters
 */
export function buildDefaultValues(
  params: ParsedParam[],
): Record<string, string> {
  const map: Record<string, string> = {};

  for (const param of params) {
    if (param.defaultValue === undefined) {
      continue;
    }

    const defaultValue = param.defaultValue as unknown;

    if (param.type === "object" || param.type === "array") {
      try {
        map[param.name] = JSON.stringify(defaultValue);
      } catch {
        map[param.name] = "";
      }
      continue;
    }

    if (param.type === "boolean") {
      map[param.name] = String(defaultValue === true);
      continue;
    }

    map[param.name] = String(defaultValue);
  }

  return map;
}

/**
 * Get placeholder text for parameter input
 */
export function getParameterPlaceholder(param: ParsedParam): string {
  switch (param.type) {
    case "integer":
    case "number":
      return "Number";
    case "boolean":
      return "Choose true/false";
    case "object":
      return "JSON object";
    case "array":
      return `JSON array${param.itemsType ? ` of ${param.itemsType}s` : ""}`;
    default:
      return "Text";
  }
}

/**
 * Get input mode for parameter input
 */
export function getParameterInputMode(
  param: ParsedParam,
): "numeric" | undefined {
  if (param.type === "integer" || param.type === "number") {
    return "numeric";
  }
  return undefined;
}

/**
 * Format parameter value for display
 */
export function formatParameterValueForDisplay(
  value: unknown,
  param: ParsedParam,
): string {
  if (value == null) {
    return "";
  }

  if (param.type === "object" || param.type === "array") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  return String(value);
}

/**
 * Get parameter display label
 */
export function getParameterLabel(param: ParsedParam): string {
  return param.required ? param.name : `${param.name} (optional)`;
}
